import React, { useState, useEffect, useRef } from "react";
import "./Ticket2.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
axios.defaults.withCredentials = true;

const StatusBadge = ({ status }) => {
  const getStatusClass = () => {
    switch (status?.toLowerCase()) {
      case "open":
        return "status status-open";
      case "foundcause":
        return "status status-foundcause";
      case "nextfollowup":
        return "status status-nextfollowup";
      case "closed":
        return "status status-closed";
      default:
        return "status";
    }
  };
  return <span className={getStatusClass()}>{status}</span>;
};

const PriorityIndicator = ({ level }) => {
  const getPriorityClass = () => {
    switch (level?.toLowerCase()) {
      case "high":
        return "priority priority-high";
      case "critical":
        return "priority priority-critical";
      case "medium":
        return "priority priority-medium";
      case "low":
        return "priority priority-low";
      default:
        return "priority";
    }
  };
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <span className={getPriorityClass()}></span>
      {level}
    </div>
  );
};

const normalizeStatus = (s = "") =>
  s
    .toString()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const FILTER_TO_MATCHES = {
  all: null,
  notcomplete: new Set(["open", "foundcause", "foudcause", "nextfollowup"]),
  complete: new Set(["closed"]),
  open: new Set(["open"]),
  foundcause: new Set(["foundcause", "foudcause"]),
  nextfollowup: new Set(["nextfollowup"]),
};

const TicketTable = ({
  onRowClick,
  selectedTicketId,
  statusFilter = "all",
}) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const previousDataRef = useRef(null);
  const formatDate = (dateString) => {
    if (!dateString || typeof dateString !== "string") return "Not Available";

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      console.warn("⚠️ Invalid date:", dateString);
      return "Not Available";
    }

    return date.toLocaleString("th-TH", {
      timeZone: "Asia/Bangkok",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    const fetchTicketsAndCheckStatus = async () => {
      try {
        const response = await axios.get("/ticketadmin", {
          withCredentials: true,
        });

        const fetchedTickets = response.data;

        if (
          JSON.stringify(fetchedTickets) !==
          JSON.stringify(previousDataRef.current)
        ) {
          setTickets(fetchedTickets);
          previousDataRef.current = fetchedTickets;
        }

        setError(null);
      } catch (error) {
        console.error("Error fetching tickets:", error);
        setError("Failed to load tickets. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchTicketsAndCheckStatus();
  }, []);
  const navigate = useNavigate();

  if (loading) return <div className="loading">Loading tickets...</div>;
  if (error) return <div className="error">{error}</div>;

  const displayedTickets =
    statusFilter === "all"
      ? tickets
      : tickets.filter((t) =>
          FILTER_TO_MATCHES[statusFilter].has(normalizeStatus(t?.Status))
        );

  return (
    <div className="ticket2-table">
      <table>
        <thead>
          <tr>
            <th>Ticket</th>
            <th>Create Date </th>
            <th>issue summary</th>
            <th>From</th>
            <th>Company</th>
            <th>Priority</th>
            <th>Status</th>
            <th>View Ticket</th>
          </tr>
        </thead>
        <tbody>
          {displayedTickets.map((ticket) => (
            <React.Fragment key={ticket.id}>
              <tr
                className={selectedTicketId === ticket.id ? "selected-row" : ""}
                onClick={() => onRowClick(ticket.id, ticket.id)}
              >
                <td>{ticket.id}</td>
                <td>{formatDate(ticket.Date)}</td>
                <td>{ticket.IssueSummary}</td>
                <td
                  style={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    maxWidth: "200px",
                  }}
                >
                  {ticket.FullName}
                </td>
                <td
                  style={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    maxWidth: "200px",
                  }}
                >
                  {ticket.CompanyName}
                </td>
                <td>
                  <PriorityIndicator level={ticket.SeverityLevels} />
                </td>
                <td>
                  <StatusBadge status={ticket.Status} />
                  {normalizeStatus(ticket.Status) === "nextfollowup" &&
                    ticket.NextFollowupDate && (
                      <div
                        style={{
                          marginTop: "4px",
                          fontSize: "12px",
                          color: "#0f0e0dff",
                          textAlign: "center",
                          width: "100%",
                        }}
                      >
                        {new Date(ticket.NextFollowupDate).toLocaleDateString(
                          "th-TH",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          }
                        )}
                      </div>
                    )}
                </td>
                <td colSpan="7" style={{ textAlign: "center" }}>
                  <button
                    className="btn-primary"
                    onClick={() => {
                      navigate(`/admin/ticketadmin/${ticket.id}`, {
                        state: { ticket },
                      });
                    }}
                  >
                    View
                  </button>
                </td>
              </tr>
              <tr></tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Ticket2 = () => {
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedTicketNumber, setSelectedTicketNumber] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const handleRowClick = (ticketId, ticketNumber) => {
    if (selectedTicket === ticketId && selectedTicketNumber === ticketNumber) {
      setSelectedTicket(null);
      setSelectedTicketNumber(null);
    } else {
      setSelectedTicket(ticketId);
      setSelectedTicketNumber(ticketNumber);
    }
  };

  return (
    <div className="ticket-system">
      <div className="sla-container">
        <h3 className="sla-title">📊 SLA Overview</h3>
        <table className="sla-table">
          <thead className="sla-header">
            <tr>
              <th>SLA</th>
              <th>Severity</th>
              <th>Day</th>
              <th>Respond</th>
              <th>L1 Follow-up</th>
              <th>L2 Alert</th>
              <th>L3 Escalate</th>
              <th>Resolve</th>
            </tr>
          </thead>
          <tbody>
            <tr className="sla-row-high">
              <td rowSpan="3">Platinum (7x24x4)</td>
              <td>High</td>
              <td>7 Days, 24 Hour</td>
              <td>4</td>
              <td>0.5</td>
              <td>1</td>
              <td>4</td>
              <td>4</td>
            </tr>
            <tr className="sla-row-medium">
              <td>Medium</td>
              <td>7 Days, 24 Hour</td>
              <td>4</td>
              <td>1</td>
              <td>4</td>
              <td>4</td>
              <td>4</td>
            </tr>
            <tr className="sla-row-low">
              <td>Low</td>
              <td>7 Days, 24 Hour</td>
              <td>4</td>
              <td>2</td>
              <td>NBD</td>
              <td>3 NBD</td>
              <td>4</td>
            </tr>

            <tr className="sla-row-high">
              <td rowSpan="3">Gold (7x24xNBD)</td>
              <td>High</td>
              <td>7 Days, 24 Hour</td>
              <td>NBD</td>
              <td>1</td>
              <td>2</td>
              <td>4</td>
              <td>NBD</td>
            </tr>
            <tr className="sla-row-medium">
              <td>Medium</td>
              <td>7 Days, 24 Hour</td>
              <td>NBD</td>
              <td>2</td>
              <td>4</td>
              <td>NBD</td>
              <td>NBD</td>
            </tr>
            <tr className="sla-row-low">
              <td>Low</td>
              <td>7 Days, 24 Hour</td>
              <td>NBD</td>
              <td>4</td>
              <td>3 NBD</td>
              <td>5 NBD</td>
              <td>NBD</td>
            </tr>

            <tr className="sla-row-high">
              <td rowSpan="3">Silver (5x8xNBD)</td>
              <td>High</td>
              <td>M–F, 8.30-17.30</td>
              <td>NBD</td>
              <td>2</td>
              <td>4</td>
              <td>2 NBD</td>
              <td>NBD</td>
            </tr>
            <tr className="sla-row-medium">
              <td>Medium</td>
              <td>M–F, 8.30-17.31</td>
              <td>NBD</td>
              <td>4</td>
              <td>NBD</td>
              <td>2 NBD</td>
              <td>NBD</td>
            </tr>
            <tr className="sla-row-low">
              <td>Low</td>
              <td>M–F, 8.30-17.32</td>
              <td>NBD</td>
              <td>8</td>
              <td>3 NBD</td>
              <td>5 NBD</td>
              <td>NBD</td>
            </tr>
          </tbody>
        </table>

        <div className="sla-info">
          <p>
            <strong>Definition of Priority:</strong>
          </p>
          <ul>
            <li>
              <strong className="high">High</strong>: System Down
            </li>
            <li>
              <strong className="medium">Medium</strong>: Performance Down
            </li>
            <li>
              <strong className="low">Low</strong>: General Question
            </li>
          </ul>
          <p>
            <strong>Next Business Day's Clock:</strong>
            <br />
            • Business Day: Monday - Friday
            <br />
            • Operation Period: 8:30 - 17:30
            <br />• Next Business Day : For every Case come in after 14.00,
            clock L1 Alert NBD.
          </p>
        </div>
      </div>

      <div className="title-bar">
        <h2 className="system-title">Ticket Management System</h2>

        <div className="sort-controls">
          <label htmlFor="statusFilter">Status:</label>
          <select
            className="status-select"
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="complete">Complete</option>
            <option value="notcomplete">Not Complete</option>
            <option value="open">Open</option>
            <option value="foundcause">Found Cause</option>
            <option value="nextfollowup">Next Follow Up</option>
          </select>
        </div>
      </div>

      <TicketTable
        key={statusFilter}
        onRowClick={handleRowClick}
        selectedTicketId={selectedTicket}
        selectedTicketNumber={selectedTicketNumber}
        statusFilter={statusFilter}
      />
    </div>
  );
};

export default Ticket2;
